import { getAssetUrl } from 'lib/config/assetUrls';

const SCRIPT_SRC = getAssetUrl(
  '/web_v2/site-static/spline/spline-viewer.js',
);

const HIGH_FLOAT_FALLBACK: WebGLShaderPrecisionFormat = {
  rangeMin: 127,
  rangeMax: 127,
  precision: 23,
};

let precisionPatched = false;
let scriptPromise: Promise<void> | null = null;
let webglOk: boolean | null = null;

type NetworkInformationLike = {
  saveData?: boolean;
};

function getConnection(): NetworkInformationLike | undefined {
  const nav = navigator as Navigator & {
    connection?: NetworkInformationLike;
    mozConnection?: NetworkInformationLike;
    webkitConnection?: NetworkInformationLike;
  };
  return nav.connection ?? nav.mozConnection ?? nav.webkitConnection;
}

export function canUseSplineWebGL(): boolean {
  if (typeof window === 'undefined') return false;
  if (webglOk !== null) return webglOk;
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    webglOk = false;
    return false;
  }
  if (getConnection()?.saveData) {
    webglOk = false;
    return false;
  }

  try {
    const canvas = document.createElement('canvas');
    const gl =
      canvas.getContext('webgl2') ||
      canvas.getContext('webgl') ||
      canvas.getContext('experimental-webgl');
    webglOk = !!(
      gl && typeof (gl as WebGLRenderingContext).getParameter === 'function'
    );
    return webglOk;
  } catch {
    webglOk = false;
    return false;
  }
}

type ShaderPrecisionHost = {
  getShaderPrecisionFormat(
    shaderType: number,
    precisionType: number,
  ): WebGLShaderPrecisionFormat | null;
};

function patchPrototype(proto: ShaderPrecisionHost): void {
  const original = proto.getShaderPrecisionFormat;
  proto.getShaderPrecisionFormat = function getShaderPrecisionFormat(
    this: ShaderPrecisionHost,
    shaderType: number,
    precisionType: number,
  ) {
    try {
      return (
        original.call(this, shaderType, precisionType) ?? HIGH_FLOAT_FALLBACK
      );
    } catch {
      return HIGH_FLOAT_FALLBACK;
    }
  };
}

/** Old Three.js (Spline) reads `.precision` on a null return from Safari / lost contexts. */
export function patchShaderPrecisionFormat(): void {
  if (precisionPatched || typeof WebGLRenderingContext === 'undefined') return;
  precisionPatched = true;
  patchPrototype(WebGLRenderingContext.prototype);
  if (typeof WebGL2RenderingContext !== 'undefined') {
    patchPrototype(WebGL2RenderingContext.prototype);
  }
}

export function loadSplineViewerScript(): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('spline-viewer requires a browser'));
  }
  if (customElements.get('spline-viewer')) return Promise.resolve();
  if (scriptPromise) return scriptPromise;

  scriptPromise = new Promise<void>((resolve, reject) => {
    const existing = document.querySelector<HTMLScriptElement>(
      `script[src="${SCRIPT_SRC}"]`,
    );
    if (existing) {
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener(
        'error',
        () => reject(new Error('spline-viewer failed to load')),
        { once: true },
      );
      return;
    }

    const script = document.createElement('script');
    script.type = 'module';
    script.src = SCRIPT_SRC;
    script.onload = () => resolve();
    script.onerror = () => reject(new Error('spline-viewer failed to load'));
    document.body.appendChild(script);
  }).catch((error) => {
    scriptPromise = null;
    throw error;
  });

  return scriptPromise;
}
